import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  CircularProgress,
  Search,
} from "@totalenergiescode/mobility-business-rev-design-system";
import {
  CardProTemplateData,
  useFormDataContext,
} from "@totalenergiescode/mobility-business-rev-form-data/components";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { prettifyJson } from "@/components/FormData/shared/json";
import { ErrorBanner } from "@/components/FormData/templates/ModificationTemplate/ErrorBanner/component";
import { PreviewTemplate } from "@/components/FormData/templates/PreviewTemplate";
import { StyledForm } from "@/components/formDataInjector/styles";
import { ControlledTextField } from "@/components/shared/ControlledTextField";

import {
  StyledAccountContainer,
  StyledDrawer,
  StyledDrawerInnerContainer,
  StyledHeaderText,
  StyledInputContainer,
  StyledRoundedTopLeftContainer,
  StyledSearchButtonContainer,
  StyledSection,
} from "./styles";
import {
  getModificationTemplateFormSchema,
  GetModificationTemplateFormType,
} from "./types";

const ModificationTemplate = ({
  isOpen,
  onClose,
  onTemplateSelected,
}: {
  isOpen: boolean;
  onClose: () => void;
  onTemplateSelected: (json: string) => void;
}) => {
  const { providers: formDataProviders } = useFormDataContext();
  const { t: tFormData } = useTranslation("formData");
  const { control, handleSubmit, getValues } =
    useForm<GetModificationTemplateFormType>({
      resolver: zodResolver(getModificationTemplateFormSchema),
      defaultValues: {
        ledgerId: "",
        accountId: "",
      },
    });

  const onSubmit = (values: GetModificationTemplateFormType) => {
    console.log(values);
  };

  // Data states
  const [templateData, setTemplateData] = useState<CardProTemplateData | null>(
    null,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // UI states
  const [ledgerLoading, setLedgerLoading] = useState<boolean>(false);

  const handleGetLedgerModificationData = async () => {
    setLedgerLoading(true);
    setTemplateData(null);
    setErrorMessage(null);
    const ledgerId = getValues("ledgerId").trim();
    const accountId = getValues("accountId")?.trim();

    formDataProviders.modificationTemplate
      .search(accountId, ledgerId)
      .then((result) => {
        setTemplateData(result);
      })
      .catch((error: unknown) => {
        console.error("Ledger modification data search failed: ", error);
        setTemplateData(null);
        setErrorMessage(
          tFormData("formData.modificationTemplate.errors.unknown"),
        );
      })
      .finally(() => setLedgerLoading(false));
  };

  const handleLoadTemplate = () => {
    if (templateData) {
      onTemplateSelected(prettifyJson(JSON.parse(templateData.rawFormData)));
      onClose();
    }
  };

  return (
    <StyledDrawer
      dataTestId={"formdata__modification-template-drawer"}
      isOpen={isOpen}
      onClose={onClose}
    >
      <StyledHeaderText variant='title5'>
        {tFormData("formData.modificationTemplate.title")}
      </StyledHeaderText>

      <StyledRoundedTopLeftContainer>
        <StyledDrawerInnerContainer>
          <StyledForm onSubmit={handleSubmit(onSubmit)}>
            <StyledSection>
              <StyledAccountContainer>
                <StyledInputContainer>
                  <ControlledTextField
                    name='accountId'
                    control={control}
                    namespace='formData'
                    textFieldProps={{
                      placeholder: "ex: 86f2db72-4488-5120-9447-5ad84f5fad72",
                      label: tFormData(
                        "formData.modificationTemplate.accountId",
                      ),
                      dataTestId:
                        "formdata__modification-template-form_accountId-input",
                    }}
                  />
                </StyledInputContainer>
                <StyledInputContainer>
                  <ControlledTextField
                    name='ledgerId'
                    control={control}
                    namespace='formData'
                    textFieldProps={{
                      placeholder: "ex :22221111-1111-1111-1111-111111111128",
                      label: tFormData(
                        "formData.modificationTemplate.ledgerId",
                      ),
                      dataTestId:
                        "formdata__modification-template-form_ledgerId-input",
                    }}
                  />
                </StyledInputContainer>

                <StyledSearchButtonContainer>
                  <Button
                    variant='primary'
                    size='m'
                    isLowercase
                    leftIcon={
                      ledgerLoading ? (
                        <CircularProgress size={14} color='inherit' />
                      ) : (
                        <Search width={14} height={14} color='white' />
                      )
                    }
                    disabled={ledgerLoading}
                    buttonText={tFormData(
                      "formData.modificationTemplate.buttons.search",
                    )}
                    onClick={() => {
                      handleGetLedgerModificationData();
                    }}
                  />
                </StyledSearchButtonContainer>

                {/* Error Banner */}
                {errorMessage && (
                  <ErrorBanner
                    errorMessage={errorMessage}
                    tFormData={tFormData}
                  />
                )}

                {/* Template Preview Section */}
                {templateData && (
                  <PreviewTemplate
                    variant='modification'
                    templateData={templateData}
                    onLoad={handleLoadTemplate}
                  />
                )}
              </StyledAccountContainer>
            </StyledSection>
          </StyledForm>
        </StyledDrawerInnerContainer>
      </StyledRoundedTopLeftContainer>
    </StyledDrawer>
  );
};

export default ModificationTemplate;
